import { ButtonAction } from '@heidi/analytics/vocabulary';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { useSidebar } from '@heidi/design-system/page-shell';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Check, Settings } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  type JoinedWorkspaceRelation,
  type WorkspaceInvitationView,
  type WorkspaceShellButtonAction,
} from '../../contracts';
import {
  pickDefaultWorkspaceId,
  switchWorkspace,
  useQueryUser,
  useQueryWorkspaceInvitations,
  useQueryWorkspaces,
  useWorkspaceId,
} from '../../data';
import { trackWorkspaceShellEvent } from '../../data/analytics/track-workspace-shell-event';
import {
  useIsWorkspaceSettingsEnabled,
  useIsWorkspaceSwitcherEnabled,
} from '../../flags';
import { JoinWorkspaceDialog } from '../join-workspace-dialog/join-workspace-dialog';
import { SwitchWhileRecordingDialog } from '../workspace-switch/switch-while-recording-dialog';
import { useSwitchWorkspaceGuard } from '../workspace-switch/use-switch-workspace-guard';
import { WorkspaceAvatar } from '../workspace/workspace-avatar';
import { type WorkspaceTopMenuPorts } from './ports';
import { useHoverDwellMenu } from './use-hover-dwell-menu';

/**
 * Top-of-sidebar workspace switcher (gated by `useIsWorkspaceSwitcherEnabled`
 * at the mount point): active workspace identity, a Settings entry, the switch
 * list, and any pending invitations to join.
 *
 * Sits below the Heidi logo by default. With `inHeader` it takes the logo's
 * slot in the sidebar header (ribbon nav only), compacting to an avatar-only
 * target when collapsed; while `GET /workspaces` resolves, the mount point
 * renders `SideNav`'s skeleton instead of the logo so the header never
 * shape-swaps from logo to avatar.
 *
 * Single-workspace users don't get the "Workspaces" switch list — the header
 * already identifies the sole workspace.
 *
 * The recording check, the settings modal and the join dialog's app half cross
 * as `ports`, bound app-side.
 */
export const WorkspaceTopMenu = ({
  inHeader = false,
  ports,
}: {
  inHeader?: boolean;
  ports: WorkspaceTopMenuPorts;
}) => {
  const intl = useIntl();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const isHeaderCompact = inHeader && isCollapsed;
  const { data } = useQueryWorkspaces();
  const { data: invitationsData } = useQueryWorkspaceInvitations();
  const { data: userEmail } = useQueryUser((user) => user.email);
  const workspaceId = useWorkspaceId();
  const {
    menuOpen,
    isModal,
    handleOpenChange,
    closeMenu,
    refocusGuard,
    triggerProps,
    contentProps,
  } = useHoverDwellMenu();
  const [pendingInvitation, setPendingInvitation] =
    useState<WorkspaceInvitationView | null>(null);
  // The settings-IA gate: the switcher AND the settings flag, both this domain's.
  const isWorkspaceSwitcherEnabled = useIsWorkspaceSwitcherEnabled();
  const isWorkspaceSettingsEnabled = useIsWorkspaceSettingsEnabled();
  const isWorkspaceSettingsIaEnabled =
    isWorkspaceSwitcherEnabled && isWorkspaceSettingsEnabled;
  // New dispatch sites; the emitter opts them out of Amplitude.
  const trackTopMenuButton = useCallback(
    (action: WorkspaceShellButtonAction, label: string) => {
      trackWorkspaceShellEvent({
        event_name: 'button_clicked',
        props: { screen_name: 'workspace_top_menu', action, label },
      });
    },
    []
  );
  const performSwitch = useCallback((workspace: JoinedWorkspaceRelation) => {
    switchWorkspace({
      workspaceId: workspace.workspace_id,
      workspaceName: workspace.workspace_name,
      iconUrl: workspace.icon_url,
    });
    window.location.replace('/scribe');
  }, []);
  const switchGuard = useSwitchWorkspaceGuard({
    isRecordingActive: ports.isRecordingActive,
    performSwitch,
  });

  useEffect(() => {
    if (switchGuard.isRefused) {
      trackTopMenuButton(
        ButtonAction.WorkspaceTopMenuSwitchBlockedRecording,
        'switch_blocked'
      );
    }
  }, [switchGuard.isRefused, trackTopMenuButton]);

  const workspaces = data?.workspaces ?? [];
  const invitations = invitationsData?.invitations ?? [];
  const firstWorkspace = workspaces[0];
  const hasMultipleWorkspaces = workspaces.length > 1;

  if (!firstWorkspace) {
    return null;
  }

  const defaultWorkspaceId = pickDefaultWorkspaceId(workspaces);
  const currentWorkspace =
    workspaces.find((workspace) => workspace.workspace_id === workspaceId) ??
    workspaces.find((workspace) => workspace.workspace_id === defaultWorkspaceId) ??
    firstWorkspace;

  const selectWorkspace = (workspace: JoinedWorkspaceRelation) => {
    if (workspace.workspace_id === currentWorkspace.workspace_id) {
      return;
    }
    trackTopMenuButton(ButtonAction.WorkspaceTopMenuWorkspaceSelected, 'workspace');
    closeMenu();
    void switchGuard.requestSwitch(workspace);
  };

  const joinWorkspace = (invitation: WorkspaceInvitationView) => {
    if (!userEmail) {
      return;
    }
    trackTopMenuButton(ButtonAction.WorkspaceTopMenuJoinTeam, 'join_team');
    closeMenu();
    setPendingInvitation(invitation);
  };

  const openSettings = () => {
    closeMenu();
    ports.onOpenSettings();
  };

  return (
    <>
      <JoinWorkspaceDialog
        invitation={pendingInvitation}
        onClose={() => setPendingInvitation(null)}
        ports={ports.joinDialog}
      />
      <SwitchWhileRecordingDialog
        open={switchGuard.isRefused}
        onOpenChange={(open) => {
          if (!open) switchGuard.dismiss();
        }}
      />
      {/* modal is pinned once per open-session (see `useHoverDwellMenu`), not
          read live — Radix picks a structurally different content tree for
          modal vs non-modal on every render, so flipping it while already
          open would remount the popup mid-interaction. Hover-opened sessions
          stay non-modal: Radix's modal content locks body scroll and
          aria-hides the rest of the page on open, which can shift layout
          under a stationary cursor — that shift alone fires a spurious
          pointerleave on the trigger, closing a hover-opened menu, whose
          close then shifts things back and fires a spurious pointerenter,
          reopening it forever. Click/keyboard-opened sessions keep Radix's
          normal modal behavior (focus trap, aria-hide, scroll lock) — only
          the hover path needs the workaround. */}
      <DropdownMenu open={menuOpen} onOpenChange={handleOpenChange} modal={isModal}>
        <DropdownMenuTrigger asChild>
          <Button
            size="lg"
            type="button"
            variant="ghost"
            disableAutomaticTypographyClasses
            className={cn(
              'h-auto w-full justify-start gap-2 rounded-md p-2 font-normal',
              // MUL-3522: the collapsed avatar drops the DS Button hover/active
              // plate for a plain opacity dim, and rounds the Button's own
              // focus ring (kept, WCAG 2.4.7) to the avatar's circle rather
              // than the square the plate drew. `select-none` stops a fast
              // double-click turning into a text-drag over the sidebar.
              isCollapsed
                ? 'select-none justify-center rounded-full p-1 transition-opacity duration-200 ease-out hover:opacity-90 motion-reduce:transition-none'
                : 'hover:bg-background-secondary-hover active:bg-background-secondary-pressed',
              // MUL-3491's header-compact footprint still centres a padding-free
              // avatar in the ribbon header.
              isHeaderCompact && 'mx-auto w-auto p-0'
            )}
            data-testid="workspace-top-menu-trigger"
            aria-label={intl.formatMessage(
              { defaultMessage: 'Workspace: {name}', id: 'JLoa05jm2l' },
              { name: currentWorkspace.workspace_name }
            )}
            onPointerEnter={triggerProps.onPointerEnter}
            onPointerLeave={triggerProps.onPointerLeave}
            onPointerDown={() => {
              // MUL-3522 refocus guard also hangs off trigger pointerdown; its
              // handler is composed in here because this explicit prop would
              // otherwise shadow the one `refocusGuard.triggerProps` spreads.
              refocusGuard.triggerProps.onPointerDown();
              triggerProps.onPointerDown();
            }}
          >
            <WorkspaceAvatar
              id={currentWorkspace.workspace_id}
              name={currentWorkspace.workspace_name}
              iconUrl={currentWorkspace.icon_url}
            />
            {!isCollapsed && (
              <span className="flex min-w-0 flex-1 flex-col items-start">
                <TypographyCaptionBold className="truncate text-text-primary">
                  {currentWorkspace.workspace_name}
                </TypographyCaptionBold>
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          sideOffset={8}
          className="max-h-[var(--radix-dropdown-menu-content-available-height)] w-72 overflow-y-auto overflow-x-hidden p-2"
          onPointerEnter={contentProps.onPointerEnter}
          onPointerLeave={contentProps.onPointerLeave}
          onFocusOutside={contentProps.onFocusOutside}
          {...refocusGuard.contentProps}
        >
          <DropdownMenuLabel className="flex items-center gap-3 p-2">
            <WorkspaceAvatar
              id={currentWorkspace.workspace_id}
              name={currentWorkspace.workspace_name}
              iconUrl={currentWorkspace.icon_url}
            />
            <span className="flex min-w-0 flex-1 flex-col">
              <TypographyP2Bold className="truncate text-text-primary">
                {currentWorkspace.workspace_name}
              </TypographyP2Bold>
            </span>
          </DropdownMenuLabel>

          <DropdownMenuItem
            onClick={openSettings}
            data-testid="workspace-top-menu-settings"
          >
            <Settings size={16} className="shrink-0" />
            {isWorkspaceSettingsIaEnabled
              ? intl.formatMessage({
                  defaultMessage: 'Workspace settings',
                  id: 'FhaqDv2irP',
                })
              : intl.formatMessage({ defaultMessage: 'Settings', id: 'D3idYvSLF9' })}
          </DropdownMenuItem>

          {hasMultipleWorkspaces && (
            <>
              <DropdownMenuSeparator />

              <DropdownMenuLabel className="text-text-tertiary">
                <TypographyCaptionBold>
                  {intl.formatMessage({ defaultMessage: 'Workspaces', id: 'DVb13UpLgn' })}
                </TypographyCaptionBold>
              </DropdownMenuLabel>
              {workspaces.map((workspace) => {
                const isActive = workspace.workspace_id === currentWorkspace.workspace_id;
                return (
                  <DropdownMenuItem
                    key={workspace.workspace_id}
                    onClick={() => selectWorkspace(workspace)}
                    data-testid={`workspace-top-menu-item-${workspace.workspace_id}`}
                    className="gap-3"
                  >
                    <WorkspaceAvatar
                      id={workspace.workspace_id}
                      name={workspace.workspace_name}
                      iconUrl={workspace.icon_url}
                      size="md"
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <TypographyCaptionBold className="truncate text-text-primary">
                        {workspace.workspace_name}
                      </TypographyCaptionBold>
                    </span>
                    {isActive && (
                      <Check size={16} className="shrink-0 text-text-secondary" />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </>
          )}

          {invitations.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-text-tertiary">
                <TypographyCaptionBold>
                  {intl.formatMessage({
                    defaultMessage: 'Workspace invites',
                    id: 'LosC403BDI',
                  })}
                </TypographyCaptionBold>
              </DropdownMenuLabel>
              {invitations.map((invitation) => (
                <div
                  key={invitation.invitation_id}
                  className="flex items-center gap-3 px-2 py-1.5"
                  data-testid={`workspace-top-menu-invite-${invitation.invitation_id}`}
                >
                  <WorkspaceAvatar
                    id={invitation.workspace_id ?? invitation.team_id}
                    name={invitation.workspace_name}
                    iconUrl={invitation.icon_url}
                    size="md"
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <TypographyCaptionBold className="truncate text-text-primary">
                      {invitation.workspace_name}
                    </TypographyCaptionBold>
                    <TypographyCaption className="truncate text-text-secondary">
                      {intl.formatMessage({
                        defaultMessage: 'Sent you an invite',
                        id: 'hzK+szD6k9',
                      })}
                    </TypographyCaption>
                  </span>
                  <Button
                    size="sm"
                    onClick={() => joinWorkspace(invitation)}
                    disabled={!userEmail}
                    data-testid={`workspace-top-menu-join-${invitation.invitation_id}`}
                  >
                    {intl.formatMessage({ defaultMessage: 'Join', id: 'P6PLpip+L0' })}
                  </Button>
                </div>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
